/**
 * Audio uploader (admin/dev, 2026-06-14) — Tornike uploads real human-voice
 * audio files to replace TTS, instead of recording in-browser. Each file
 * uploads to POST /api/admin/audio-upload?key=<audio_key> (stored in R2 under
 * rec/). Progress persists in localStorage so he can stop and resume.
 *
 * Two ways to upload:
 *   1. Per-phrase: pick the file for the current phrase, listen, save → next.
 *   2. Bulk: select many files named by their number (1.mp3, 2.mp3, …); each
 *      is matched to the phrase with that number and uploaded.
 *
 * Route: /audio-record (must be logged in as an admin — the upload is gated).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, FileTrigger } from '@fluent/ui';
import { supabase } from '../lib/supabase.js';
import { unitClips } from '../content/unitClips.js';

// Unit is selectable via ?unit=unit-02-people (default unit-01-basics) so the
// same recorder works for every unit. Progress is keyed per unit.
const UNIT =
  (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('unit')) ||
  'unit-01-basics';
// v2: progress reset 2026-06-14 after the female-narrator uploads were deleted
// (man is now the main/narrator voice). Bumping the key clears stale progress.
const DONE_KEY = `fluent.rec.${UNIT}.v2.done`;
const UNIT_LABEL = UNIT.replace('unit-0', 'Unit ').replace(/-.*/, '');

async function uploadClip(key: string, file: Blob): Promise<void> {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  if (!token) throw new Error('login_required');
  const res = await fetch(`/api/admin/audio-upload?key=${encodeURIComponent(key)}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'content-type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!res.ok) {
    const j = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(j.error ?? String(res.status));
  }
}

export function AudioRecord() {
  const clips = useMemo(() => unitClips(UNIT), []);
  const [done, setDone] = useState<Set<string>>(() => {
    try {
      return new Set<string>(JSON.parse(localStorage.getItem(DONE_KEY) ?? '[]'));
    } catch {
      return new Set();
    }
  });
  const [idx, setIdx] = useState(0);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [bulkStatus, setBulkStatus] = useState('');
  const blobRef = useRef<Blob | null>(null);

  const clip = clips[idx];

  useEffect(() => {
    const first = clips.findIndex((c) => !done.has(c.key));
    if (first >= 0) setIdx(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistDone = useCallback((next: Set<string>) => {
    setDone(next);
    localStorage.setItem(DONE_KEY, JSON.stringify([...next]));
  }, []);

  const clearBlob = useCallback(() => {
    blobRef.current = null;
    setBlobUrl((u) => {
      if (u) URL.revokeObjectURL(u);
      return null;
    });
  }, []);

  const onPickOne = useCallback((file: File | undefined) => {
    if (!file) return;
    blobRef.current = file;
    setBlobUrl(URL.createObjectURL(file));
    setStatus(`არჩეულია: ${file.name}`);
  }, []);

  const play = useCallback(() => {
    if (blobUrl) void new Audio(blobUrl).play();
  }, [blobUrl]);

  const advance = useCallback(() => {
    clearBlob();
    setStatus('');
    const next = clips.findIndex((c, i) => i > idx && !done.has(c.key));
    setIdx(next >= 0 ? next : Math.min(idx + 1, clips.length - 1));
  }, [clips, idx, done, clearBlob]);

  const saveNext = useCallback(async () => {
    if (!blobRef.current) {
      setStatus('ჯერ აირჩიე ფაილი.');
      return;
    }
    setStatus('იტვირთება…');
    try {
      await uploadClip(clip.key, blobRef.current);
      const next = new Set(done);
      next.add(clip.key);
      persistDone(next);
      advance();
    } catch (e) {
      const m = (e as Error).message;
      setStatus(m === 'login_required' ? 'გთხოვ შეხვიდე ანგარიშზე (admin).' : 'შეცდომა: ' + m);
    }
  }, [clip, done, persistDone, advance]);

  const onBulk = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      const next = new Set(done);
      let okN = 0;
      const fails: string[] = [];
      const arr = files;
      for (let i = 0; i < arr.length; i++) {
        const f = arr[i];
        const m = f.name.match(/(\d+)/);
        const num = m ? parseInt(m[1], 10) : NaN;
        const target = clips[num - 1];
        setBulkStatus(`იტვირთება ${i + 1}/${arr.length}… (${f.name})`);
        if (!target || Number.isNaN(num)) {
          fails.push(`${f.name} (ნომერი ვერ ვიპოვე)`);
          continue;
        }
        try {
          await uploadClip(target.key, f);
          next.add(target.key);
          okN++;
        } catch (e) {
          fails.push(`${f.name} (${(e as Error).message})`);
        }
      }
      persistDone(next);
      setBulkStatus(`დასრულდა: ${okN} აიტვირთა${fails.length ? `, ${fails.length} ვერ: ${fails.slice(0, 5).join('; ')}` : ' ✅'}`);
    },
    [clips, done, persistDone],
  );

  if (!clip) return <div style={{ padding: 24 }}>ჩასატვირთი არაფერია.</div>;

  // Narrator + Giorgi = male (Tornike, main voice). Mariam = female (dialogues).
  const isWoman = clip.voice === 'Mariam';
  const genderLabel = isWoman ? '👩 ქალის ხმა' : '👨 კაცის ხმა';
  const voiceColor = isWoman ? '#d9582e' : '#1f7ae0';
  const isDone = done.has(clip.key);

  return (
    <div style={{ fontFamily: 'inherit', background: '#fff8ee', color: '#2b241b', minHeight: '100vh', padding: '16px 16px 56px', maxWidth: 600, marginInline: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h1 style={{ fontSize: 18, margin: '6px 0' }}>📤 ატვირთვა — {UNIT_LABEL}</h1>
        <span style={{ fontSize: 14, fontWeight: 800, color: '#4a9e00' }}>{done.size} / {clips.length}</span>
      </div>
      <div style={{ height: 6, background: '#f0e8d8', borderRadius: 3, margin: '4px 0 16px' }}>
        <div style={{ height: 6, width: `${(done.size / clips.length) * 100}%`, background: '#58cc02', borderRadius: 3 }} />
      </div>

      {/* Bulk upload */}
      <div style={{ background: '#eef6ff', border: '1px solid #cfe2fb', borderRadius: 14, padding: '12px 14px', marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#1f5fae', marginBottom: 6 }}>📁 ბევრი ფაილი ერთად</div>
        <div style={{ fontSize: 12, color: '#5a6a7a', marginBottom: 10 }}>
          დაასახელე ფაილები ნომრით (1.mp3, 2.mp3 … {clips.length}.mp3) ქვემოთ სიის მიხედვით და აირჩიე ყველა ერთად.
        </div>
        <FileTrigger accept="audio/*" multiple onSelect={(files) => void onBulk(files)}>
          {(open) => <Button variant="secondary" onClick={open}>📁 აირჩიე ბევრი ფაილი</Button>}
        </FileTrigger>
        <div style={{ minHeight: 18, marginTop: 8, fontSize: 12, color: '#1f5fae' }}>{bulkStatus}</div>
      </div>

      {/* Per-phrase upload */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 2px 10px rgba(43,36,27,.1)', textAlign: 'center' }}>
        <div style={{ fontSize: 13, color: '#8a7f6e' }}>
          ფრაზა {idx + 1} / {clips.length} · გაკვეთილი {clip.lessonPos} · {clip.skill} {isDone ? '· ✅ ატვირთულია' : ''}
        </div>
        <div style={{ fontSize: 26, fontWeight: 800, margin: '14px 0 6px' }}>{clip.en}</div>
        <div style={{ fontSize: 15, color: '#8a7f6e' }}>{clip.ka}</div>
        <div style={{ display: 'inline-block', fontSize: 16, fontWeight: 800, color: '#fff', background: voiceColor, borderRadius: 999, padding: '4px 14px', marginTop: 12 }}>
          {genderLabel}
          {clip.voice !== 'Narrator' ? ` · ${clip.voice}` : ''}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 20, flexWrap: 'wrap' }}>
          <FileTrigger accept="audio/*" onSelect={onPickOne}>
            {(open) => <Button variant="secondary" onClick={open}>📁 ფაილის არჩევა</Button>}
          </FileTrigger>
          <Button variant="neutral" onClick={play} disabled={!blobUrl}>▶ მოსმენა</Button>
          <Button variant="primary" onClick={saveNext} disabled={!blobUrl}>💾 შენახვა →</Button>
        </div>
        <div style={{ minHeight: 20, marginTop: 12, fontSize: 13, color: '#c8372e' }}>{status}</div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
        <Button variant="ghost" size="sm" onClick={() => { clearBlob(); setIdx(Math.max(0, idx - 1)); }}>← წინა</Button>
        <Button variant="ghost" size="sm" onClick={advance}>გამოტოვება →</Button>
      </div>

      {/* Numbered reference for bulk naming */}
      <div style={{ background: '#fff', borderRadius: 14, padding: '12px 14px', marginTop: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#4a9e00', marginBottom: 8 }}>📋 ნომრების სია (ფაილების დასახელებისთვის)</div>
        {clips.map((c, i) => (
          <div key={c.key} style={{ display: 'flex', gap: 8, fontSize: 13, padding: '3px 0', color: done.has(c.key) ? '#8a7f6e' : '#2b241b' }}>
            <span style={{ width: 34, fontWeight: 800, color: '#4a9e00' }}>{i + 1}.</span>
            <span style={{ flex: 1 }}>{c.en} {done.has(c.key) ? '✅' : ''}</span>
            <span style={{ color: c.voice === 'Mariam' ? '#d9582e' : '#1f7ae0', fontWeight: 700 }}>{c.voice === 'Mariam' ? '👩' : '👨'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
